import {
  addDays,
  addMonths,
  getEndOfDay,
  getEndOfMonth,
  getEndOfWeek,
  getStartOfDay,
  getStartOfMonth,
  getStartOfWeek,
  isSameDate,
} from '@mangovoice/core'
import { TimePeriodRange as CoreTimePeriodRange } from '@mangovoice/core'

export enum DateUnit {
  Days = 'day',
  Months = 'month',
  Years = 'year',
}

export type DateRange = object & {
  before?: Date
  after?: Date
}

// Presets read `now`'s local wall clock; pass a shifted Date (see
// `getWallClockNowInTimeZone`) to anchor them to another timezone's calendar.
export const DateRange = {
  empty: () => ({ before: undefined, after: undefined }) as DateRange,
  today: (now = new Date()) => {
    const after = getStartOfDay(now)
    const before = getEndOfDay(after)

    return {
      after,
      before,
    } as DateRange
  },
  yesterday: (now = new Date()) => {
    const after = addDays(getStartOfDay(now), -1)
    const before = getEndOfDay(after)

    return {
      after,
      before,
    } as DateRange
  },
  last7Days: (now = new Date()) => {
    const today = getEndOfDay(now)
    const sevenDaysAgo = addDays(getStartOfDay(now), -7)

    return {
      after: sevenDaysAgo,
      before: today,
    } as DateRange
  },
  last14Days: (now = new Date()) => {
    const today = getEndOfDay(now)
    const fourteenDaysAgo = addDays(getStartOfDay(now), -14)

    return {
      after: fourteenDaysAgo,
      before: today,
    } as DateRange
  },
  last30Days: (now = new Date()) => {
    const today = getEndOfDay(now)
    const thirtyDaysAgo = addDays(getStartOfDay(now), -30)

    return {
      after: thirtyDaysAgo,
      before: today,
    } as DateRange
  },
  last60Days: (now = new Date()) => {
    const today = getEndOfDay(now)
    const sixtyDaysAgo = addDays(getStartOfDay(now), -60)

    return {
      after: sixtyDaysAgo,
      before: today,
    } as DateRange
  },
  last90Days: (now = new Date()) => {
    const today = getEndOfDay(now)
    const ninetyDaysAgo = addDays(getStartOfDay(now), -90)

    return {
      after: ninetyDaysAgo,
      before: today,
    } as DateRange
  },
  thisWeek: (now = new Date()) => {
    const after = getStartOfWeek(now)
    const before = getEndOfWeek(after)

    return {
      after,
      before,
    } as DateRange
  },
  lastWeek: (now = new Date()) => {
    const after = addDays(getStartOfWeek(now), -7)
    const before = getEndOfWeek(after)

    return {
      after,
      before,
    } as DateRange
  },
  thisMonth: (now = new Date()) => {
    const after = getStartOfMonth(now)
    const before = getEndOfMonth(after)

    return {
      after,
      before,
    } as DateRange
  },
  lastMonth: (now = new Date()) => {
    const after = addMonths(getStartOfMonth(now), -1)
    const before = getEndOfMonth(after)

    return {
      after,
      before,
    } as DateRange
  },
  areEqual: (a?: DateRange, b?: DateRange) => {
    if (a === b) return true
    if (!a || !b) return false

    const beforeEqual = isSameDate(a.before, b.before)
    const afterEqual = isSameDate(a.after, b.after)

    return beforeEqual && afterEqual
  },
}
Object.freeze(DateRange)

export const TimePeriodRange = {
  empty: { before: undefined, after: undefined } as CoreTimePeriodRange,
}
Object.freeze(TimePeriodRange.empty)
